import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import moment from "moment";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { BondOrderHistoryResponse } from "./helpers";
import {
  getExternalTagLabel,
  getOrderInvestmentAmount,
  getOrderSource,
  isInternalOrderDeleted,
  usesExternalDeleteApi,
} from "./helpers";
import { parseDate, toINR } from "../../../Helpers/functions";
import copy from "copy-to-clipboard";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

interface DesktopViewProps {
  order: BondOrderHistoryResponse;
  aggregatedCashflows: Record<string, any[]>;
  openMain: Record<string, boolean>;
  openYear: Record<string, Record<string, boolean>>;
  setOpenMain: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setOpenYear: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, boolean>>>
  >;
  onDeleteOrder?: (order: BondOrderHistoryResponse) => void;
  deletingOrderUuid?: string | null;
}

const DesktopView: React.FC<DesktopViewProps> = ({
  order,
  aggregatedCashflows,
  openMain,
  openYear,
  setOpenMain,
  setOpenYear,
  onDeleteOrder,
  deletingOrderUuid,
}) => {
  const lastCallMoment = parseDate(order.last_call_date);
  const maturityMoment = parseDate(order.last_maturity_date);
  const firstPutOptionDate = order.put_options?.[0]?.date;
  const putMoment = parseDate(firstPutOptionDate);
  const hasMatchingPut =
    !!putMoment && !!maturityMoment && putMoment.isSame(maturityMoment, "day");
  const displayMoment =
    lastCallMoment || (hasMatchingPut ? putMoment : maturityMoment);
  const displayLabel = lastCallMoment
    ? "Call Date"
    : hasMatchingPut
    ? "Put Date"
    : "Maturity Date";
  const externalTagLabel = getExternalTagLabel(order);
  const isIpoTag = externalTagLabel === "IPO";
  const orderSource = getOrderSource(order);
  const isDeletedOrder = isInternalOrderDeleted(order);
  const deleteLabel = usesExternalDeleteApi(order)
    ? "Remove External Bond"
    : "Remove Internal Bond";
  const canDelete = Boolean(order.uuid) && !isDeletedOrder;
  const orderCashflows = aggregatedCashflows[order.uuid] || [];
  const principalPayout = orderCashflows.reduce(
    (total, year) => total + Number(year.maturity || 0),
    0
  );
  const interestPayout = orderCashflows.reduce(
    (total, year) => total + Number(year.interest || 0),
    0
  );

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          "& td": {
            borderBottom: "none",
          },
          "& td:first-of-type": {
            borderTopLeftRadius: 8,
            ...(!openMain[order.uuid] && {
              borderBottomLeftRadius: 8,
            }),
          },
          "& td:last-of-type": {
            borderTopRightRadius: 8,
            ...(!openMain[order.uuid] && {
              borderBottomRightRadius: 8,
            }),
          },
          backgroundColor: "#ffffff",
        }}
      >
        <TableCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <span>{order.security_name}</span>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {externalTagLabel && (
                <Chip
                  label={externalTagLabel}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: isIpoTag
                      ? "rgba(40, 167, 145, 0.14)"
                      : "rgba(242, 153, 74, 0.12)",
                    color: isIpoTag ? "#187e6d" : "#E67E22",
                    border: isIpoTag
                      ? "1px solid rgba(24, 126, 109, 0.32)"
                      : "1px solid rgba(230, 126, 34, 0.35)",
                  }}
                />
              )}
              {isDeletedOrder && (
                <Chip
                  label="DELETED"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "rgba(217, 48, 48, 0.12)",
                    color: "#b42318",
                    border: "1px solid rgba(217, 48, 48, 0.3)",
                  }}
                />
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell>{order.security_type}</TableCell>
        <TableCell>{order.security_name.split(" ")[0]}</TableCell>
        <TableCell>{order.frequency_of_interest}</TableCell>
        <TableCell>
          {displayMoment ? displayMoment.format("MMM DD,YYYY") : "-"} (
          {displayLabel})
        </TableCell>
        <TableCell>{order.rating}</TableCell>
        <TableCell>{order.no_of_bonds}</TableCell>
        <TableCell>{order.sell_yield}%</TableCell>
        <TableCell>{toINR(getOrderInvestmentAmount(order), 2)}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {onDeleteOrder && canDelete && (
              <Tooltip title={deleteLabel}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteOrder(order);
                    }}
                    disabled={deletingOrderUuid === order.uuid}
                  >
                    {deletingOrderUuid === order.uuid ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={() =>
                setOpenMain((prev) => ({
                  ...prev,
                  [order.uuid]: !prev[order.uuid],
                }))
              }
            >
              {openMain[order.uuid] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow
        sx={{
          position: "relative",
          top: -8,
          "& td:first-of-type": {
            ...(openMain[order.uuid] && {
              borderTop: "1px dashed rgba(224, 224, 224, 1)",
            }),
            borderBottomLeftRadius: 8,
          },
          "& td": {
            borderBottom: "none",
          },
          "& td:last-of-type": {
            borderBottomRightRadius: 8,
          },
          backgroundColor: "#ffffff",
        }}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={openMain[order.uuid]} timeout="auto" unmountOnExit>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ p: 2, maxWidth: ["90vw", "100%"] }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          opacity: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        ISIN
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "info.main",
                          }}
                        >
                          {order.isin}
                        </Typography>
                        <IconButton
                          sx={{
                            p: 0,
                            ml: 0.5,
                          }}
                          onClick={() => copy(order.isin)}
                        >
                          <ContentCopyRoundedIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          opacity: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Settlement Date
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {order.settlement_date}
                      </Typography>
                    </Grid>
                    {externalTagLabel === "EXTERNAL" && orderSource && (
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            opacity: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Source
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {orderSource}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          background: "#17c417",
                          position: "relative",
                          height: "100%",
                          width: "100%",
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 1,
                            color: "#FFFFFF",
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} textAlign={"center"}>
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Final Payout
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                {toINR(principalPayout + interestPayout, 2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography sx={{ fontWeight: 700 }}>
                                Principal Payout
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {toINR(principalPayout, 2)}
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              textAlign={"center"}
                              fontSize={24}
                              sx={{ fontWeight: 800 }}
                            >
                              +
                            </Grid>
                            <Grid item xs={5} textAlign="right">
                              <Typography sx={{ fontWeight: 700 }}>
                                Interest Payout
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {toINR(interestPayout, 2)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box
                  sx={{
                    mx: 2,
                    my: 4,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: 10,
                      fontWeight: 600,
                      fontSize: 12,
                      px: 2,
                      py: 0.3,
                      borderRadius: 1,
                      transform: "skew(-13deg)",
                      color: "#d4fc79",
                      background:
                        "linear-gradient(to right, #434343 0%, black 100%)",
                      boxShadow:
                        "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
                    }}
                  >
                    Year-on-Year Overview
                  </Typography>
                  <Table
                    size="small"
                    sx={{
                      mt: 1,
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>₹ Interest Amount</TableCell>
                        <TableCell>₹ Interest Amount Post TDS</TableCell>
                        <TableCell>₹ Redemption Amount</TableCell>
                        <TableCell>Datewise</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aggregatedCashflows[order.uuid]?.map((yearData) => (
                        <React.Fragment key={`${order.uuid}-${yearData.year}`}>
                          <TableRow
                            hover
                            sx={{
                              "& td": {
                                border: "none !important",
                              },
                            }}
                          >
                            <TableCell>{yearData.year}</TableCell>
                            <TableCell>
                              {yearData.frequency_of_interest}
                            </TableCell>
                            <TableCell>{toINR(yearData.interest, 2)}</TableCell>
                            <TableCell>
                              {toINR(yearData.interestAfterTax, 2)}
                            </TableCell>
                            <TableCell>{toINR(yearData.maturity, 2)}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setOpenYear((prev) => ({
                                    ...prev,
                                    [order.uuid]: {
                                      ...prev[order.uuid],
                                      [yearData.year]:
                                        !prev[order.uuid]?.[yearData.year],
                                    },
                                  }))
                                }
                              >
                                {openYear[order.uuid]?.[yearData.year] ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "& td": {
                                border: "none !important",
                              },
                            }}
                          >
                            <TableCell style={{ padding: 0 }} colSpan={6}>
                              <Collapse
                                in={openYear[order.uuid]?.[yearData.year]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box
                                  sx={{
                                    mx: 2,
                                    my: 4,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 2,
                                    p: 2,
                                    position: "relative",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      position: "absolute",
                                      top: -10,
                                      left: 10,
                                      fontWeight: 600,
                                      fontSize: 12,
                                      px: 2,
                                      py: 0.3,
                                      borderRadius: 1,
                                      transform: "skew(-13deg)",
                                      background:
                                        "linear-gradient(120deg, #d4fc79 0%, #96e6a1 100%)",
                                      color: "#434343",
                                      boxShadow:
                                        "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
                                    }}
                                  >
                                    Datewise Interest
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>
                                          Interest Payment Date
                                        </TableCell>
                                        <TableCell>₹ Interest Amount</TableCell>
                                        <TableCell>
                                          ₹ Interest Amount Post TDS
                                        </TableCell>
                                        <TableCell>
                                          ₹ Redemption Amount
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {Object.values(yearData.dateMap).map(
                                        (d) => {
                                          const row = d as {
                                            date: string;
                                            interest: number;
                                            interestAfterTax: number;
                                            maturity: number;
                                          };
                                          return (
                                            <TableRow hover key={row.date}>
                                              <TableCell>
                                                {moment(row.date).format(
                                                  "MMM DD,YYYY"
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {toINR(row.interest, 2)}
                                              </TableCell>
                                              <TableCell>
                                                {toINR(row.interestAfterTax, 2)}
                                              </TableCell>
                                              <TableCell>
                                                {toINR(row.maturity, 2)}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        }
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default DesktopView;
